import {
    Character,
    CharacterListVersion,
    CharacterWithVersion,
    ScaledCharacterListVersion,
    Version,
} from "@src/types/global";
import { isInArray } from "./array";

export function transformToCharacterListVersion(
    character: CharacterWithVersion | Character,
): CharacterListVersion {
    if (
        character.character_versions == null ||
        Array.isArray(character.character_versions)
    ) {
        let char = character as Character;
        return {
            id: char.id,
            version_id: char.default_version.id,
            first_name: char.first_name,
            last_name: char.last_name,
            version_name: char.default_version.version_name,
            avatar: char.avatar,
            body: char.default_version.body,
            age: char.default_version.age,
            height: char.default_version.height,
            override_height: char.default_version.override_height,
        };
    }
    let char = character as CharacterWithVersion;
    return {
        id: character.id,
        version_id: char.character_versions.id,
        first_name: char.first_name,
        last_name: char.last_name,
        version_name: char.character_versions.version_name,
        avatar: char.avatar,
        body: char.character_versions.body,
        age: char.character_versions.age,
        height: char.character_versions.height,
        override_height: char.character_versions.override_height,
    };
}

export function getDefaultVersion(character: Character): CharacterWithVersion {
    const defaultVersion = character.default_version;
    const parsedCharacter = {
        ...character,
        character_versions: defaultVersion,
    } as CharacterWithVersion;

    return parsedCharacter;
}
export const removeFromStack = (
    id: string,
    characterList: CharacterListVersion[],
) => {
    if (!isInArray(id, characterList)) {
        return characterList;
    }
    const newList = characterList.filter((el) => {
        return el.id !== id;
    });
    localStorage.setItem("characterList", JSON.stringify(newList));
    return newList;
};

export const setCharacterList = (characterList: CharacterListVersion[]) => {
    localStorage.setItem("characterList", JSON.stringify(characterList));
};
export function getVersionByName(
    character: Character,
    versionName: string,
): CharacterWithVersion {
    const characterVersions = character.character_versions!;
    const defaultVersion = characterVersions.find(
        (version) => version.version_name == versionName,
    );
    const parsedCharacter = {
        ...character,
        character_versions: defaultVersion,
    } as CharacterWithVersion;

    return parsedCharacter;
}

/**
 * Replaces the version given inside the character or adds it to the character_versions array if not exists
 * @param character - The character object to edit
 * @param version - The version to overwrite or add
 * @returns An updated, cloned object of the character
 */
export function overwriteCharacterVersion(
    character: Character,
    version: Version,
): Character {
    const characterClone = structuredClone(character);
    const versionExists = characterClone.character_versions?.findIndex(
        (v) => v.id == version.id,
    );
    if (versionExists == -1 || versionExists == undefined) {
        characterClone.character_versions!.push(version);
    } else {
        characterClone.character_versions![versionExists] = version;
    }
    return characterClone;
}

export const getInitialCharacterList = (): CharacterListVersion[] | [] => {
    const listInStorage = localStorage.getItem("characterList");
    if (listInStorage) {
        return JSON.parse(listInStorage);
    } else {
        return [];
    }
};

/**
 *  Given feet and inches, translate that into proper units
 *  of measurement
 *  @param feet
 *  @param inches
 *  @return Centimeters
 */
export function toMetric(feet: number, inches: number): number {
    const inCm = inches * 2.54;
    const ftCm = feet * 30.78;
    return Math.floor(inCm + ftCm);
}

/**
 *  Given centimeters, translate that into stuff that don't make sense
 *  @param cm Centimeters to murder
 *  @return An object containing the values for feet and inches
 */
export function toImperial(cm: number): { feet: number; inches: number } {
    // This is stupid
    // 1 - Convert all to ft
    // 2 - Convert anything past the decimal to in

    const cmToFeet = cm / 30.78;
    const units = Math.trunc(cmToFeet);
    const decimalsToInches = (cmToFeet - units) * 12;
    return { feet: units, inches: Math.round(decimalsToInches) };
}

/**
 * Returns a metric value as a feet/inch string
 */
export function toImperialAsString(cm: number): string {
    if (!cm) return "";
    const { feet, inches } = toImperial(cm);
    return `${feet}' ${inches}"`;
}

/*
 * Given an array of character versions and a control character (the base), returns that same array, now containing a "scale" property
 * We consider that the control character has a scale of 1. A smaller character will have a scale of < 1, a taller character a scale of > 1
 * @param versions Version[] The versions to scale against the base
 * @param baseVersion Version The version we use as a scale base of 1
 */
interface ScaledVersion extends Version {
    scale: number;
}
export function setVersionScales(
    versions: Version[],
    baseVersion: Version,
): ScaledVersion[] {
    const baseHeight =
        Number(baseVersion.override_height) || Number(baseVersion.height) || 0;
    versions.forEach((v) => {
        const height = Number(v.override_height) || Number(v.height) || 0;
        //@ts-ignore I know
        v.scale =
            Math.round((height / baseHeight + Number.EPSILON) * 100) / 100;
    });

    return versions as ScaledVersion[];
}

/**
 * Calculates the scale of the character given a base height
 */
export function setCharacterListVersionScales(
    characters: CharacterListVersion[],
    baseCharacter: CharacterListVersion,
): ScaledCharacterListVersion[] {
    const baseHeight =
        Number(baseCharacter.override_height) ||
        Number(baseCharacter.height) ||
        0;
    const res = characters.map((v) => {
        const copy = { ...v };

        const height = Number(v.override_height) || Number(v.height) || 0;
        //@ts-ignore I know
        copy.scale =
            Math.round((height / baseHeight + Number.EPSILON) * 100) / 100;
        return copy;
    });

    return res as ScaledCharacterListVersion[];
}
/**
 * takes a version with string traits and returns a version with array traits
 * @param version  The version to parse
 */
export function parseTraits(version: Version) {
    const untypedVersion = version;
    if (
        untypedVersion.qualities !== undefined &&
        !Array.isArray(untypedVersion.qualities)
    ) {
        version.qualities = untypedVersion.qualities.split(",");
    }
    if (
        untypedVersion.flaws !== undefined &&
        !Array.isArray(untypedVersion.flaws)
    ) {
        version.flaws = untypedVersion.flaws.split(",");
    }
    return untypedVersion;
}
/**
 * Uppercase the first letter of a word
 */
export function uppercase(string: string): string {
    return [string[0].toUpperCase(), ...string.substring(1)].join("");
}
